import type { ICompareInChartSummary } from "@/api";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNowStrict } from "date-fns";
import { GitCompareArrows, Table } from "lucide-react";
import { EmptySectionState } from "./related/EmptySectionState";
import { RelatedItemCard } from "./related/RelatedItemCard";
import { RelatedSectionHeader } from "./related/RelatedSectionHeader";

interface RelatedComparisonsSectionProps {
    isLoading: boolean;
    comparisons: ICompareInChartSummary[];
    onComparisonClick?: (comparison: ICompareInChartSummary) => void;
}

const defaultOnComparisonClick = (comparison: ICompareInChartSummary) => {
    window.open(`/organizations/comparisons/${comparison.id}`, "_blank");
};

export const RelatedComparisonsSection = ({
    isLoading,
    comparisons,
    onComparisonClick = defaultOnComparisonClick,
}: RelatedComparisonsSectionProps) => {
    if (isLoading) {
        return (
            <>
                <RelatedSectionHeader title="Related Comparisons" count={0} />
                <div className="flex flex-col gap-1.5">
                    {Array.from({ length: 3 }, (_, i) => (
                        <Skeleton
                            key={i}
                            data-testid="related-comparisons-skeleton-row"
                            className="h-13 w-full rounded-[10px]"
                        />
                    ))}
                </div>
            </>
        );
    }

    if (comparisons.length === 0) {
        return (
            <>
                <RelatedSectionHeader title="Related Comparisons" count={0} />
                <EmptySectionState
                    icon={Table}
                    title="No comparisons yet"
                    description="Add scopes from this document to a comparison."
                />
            </>
        );
    }

    return (
        <>
            <RelatedSectionHeader
                title="Related Comparisons"
                count={comparisons.length}
            />
            <div className="flex flex-col gap-1.5">
                {comparisons.map((comparison) => (
                    <RelatedItemCard
                        key={comparison.id}
                        icon={GitCompareArrows}
                        iconColorClass="bg-violet-600/10 text-violet-600"
                        title={comparison.name}
                        metadata={[
                            comparison.clause_checklist.name,
                            `${comparison.scope_count} ${comparison.scope_count === 1 ? "scope" : "scopes"}`,
                            formatDistanceToNowStrict(
                                new Date(comparison.created_at),
                                { addSuffix: true }
                            ),
                        ]}
                        onClick={() => onComparisonClick(comparison)}
                    />
                ))}
            </div>
        </>
    );
};
